import { useEffect, useState } from 'react'

import { Grid } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useLocation, useNavigate } from 'react-router-dom'

import MDBox from 'components/MDBox'
import { useMaterialUIController } from 'context'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import ManageCard from 'modules/ManageCard'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import { ROUTE_PATHS } from 'routes'

import MainForm from './components/MainForm'
import { defaultExclusionForm, ExclusionListFormContext } from './constants'
import { ExclusionForm } from '../types'
import { postNewExclusion } from '../utils'

function ManageExclusionForm() {
	const [controller] = useMaterialUIController()
	const { session } = controller

	const [exclusionForm, setExclusionForm] = useState(defaultExclusionForm)
	const [formEdited, setFormEdited] = useState<boolean>(false)
	const [showError, setShowError] = useState<boolean>(false)
	const navigate = useNavigate()

	const location = useLocation()
	const locationPathNameParts = location.pathname.split('/')
	const number = locationPathNameParts[locationPathNameParts.length - 1]
	const isNew = number === 'new'
	const numberRegex = /^\+44\d{10}$/

	const fetchData = async () => {
		const response = await axios.get<ExclusionForm>(
			`${process.env.REACT_APP_API_URL}/exclusion-list/item`,
			{
				params: { number },
				headers: {
					Authorization: await session.user.getIdToken(),
				},
			}
		)
		return response.data
	}

	const deleteItem = async () => {
		const response = await axios.delete<ExclusionForm>(
			`${process.env.REACT_APP_API_URL}/exclusion-list/item`,
			{
				params: { number },
				headers: {
					Authorization: await session.user.getIdToken(),
				},
			}
		)
		return response.data
	}

	const { data, isLoading, error } = useQuery({
		queryKey: ['exclusion-list-item', number],
		queryFn: fetchData,
		enabled: !isNew,
	})

	useEffect(() => {
		setExclusionForm(data || defaultExclusionForm)
	}, [data, isNew])

	return (
		<ExclusionListFormContext.Provider
			value={{
				exclusionForm,
				setExclusionForm,
				formEdited,
				setFormEdited,
				showError,
				setShowError,
			}}
		>
			<DashboardLayout>
				<DashboardNavbar isMini />
				<MDBox mt={4}>
					<Grid container spacing={3}>
						<Grid item xs={12} lg={9}>
							<MDBox mb={3}>
								{isNew || (!isLoading && !error) ? <MainForm /> : <LoadingComponent type="box" />}
							</MDBox>
						</Grid>
						<Grid item xs={12} lg={3}>
							<Grid container spacing={3}>
								<Grid item xs={12}>
									<ManageCard
										formEdited={formEdited}
										setFormEdited={setFormEdited}
										save={{
											onClicked: async () => {
												if (numberRegex.test(exclusionForm.phone_number)) {
													await postNewExclusion(session.user, [exclusionForm])
												} else {
													setShowError(true)
													throw new Error('Invalid phone number format')
												}
											},
										}}
										deleteItem={{
											visible: !isNew,
											onClicked: async () => {
												await deleteItem()
												navigate(ROUTE_PATHS.EXCLUSION_LIST.BASE)
											},
										}}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</MDBox>
			</DashboardLayout>
		</ExclusionListFormContext.Provider>
	)
}

export default ManageExclusionForm
